<script setup>
import { ref, computed, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  elements:      { type: Array,   default: () => [] },
  estimation:    { type: Object,  default: () => ({}) },
  projet:        { type: Object,  default: () => ({}) },
  partie_suivie: { type: Object,  default: () => ({}) },
  can_see_costs: { type: Boolean, default: false },
  can_update:    { type: Boolean, default: false },
  can_create:    { type: Boolean, default: false },
  can_import:    { type: Boolean, default: false },
  new_url:       { type: String,  default: '#' },
  import_url:    { type: String,  default: '#' },
  back_url:      { type: String,  default: '#' },
})

const openDropdown = ref(null)
function toggleDropdown(id) {
  openDropdown.value = openDropdown.value === id ? null : id
}

const search      = ref('')
const pageSize    = ref(10)
const currentPage = ref(1)
const sortKey     = ref('code')
const sortDir     = ref('asc')

watch([search, pageSize, sortKey, sortDir], () => { currentPage.value = 1 })

function toggleSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = 'asc'
  }
}

const filtered = computed(() => {
  let list = props.elements
  if (search.value.trim()) {
    const q = search.value.toLowerCase()
    list = list.filter(p =>
      (p.code || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q) ||
      (p.unite || '').toLowerCase().includes(q)
    )
  }
  return [...list].sort((a, b) => {
    let aVal = a[sortKey.value] ?? ''
    let bVal = b[sortKey.value] ?? ''
    if (typeof aVal === 'string') aVal = aVal.toLowerCase()
    if (typeof bVal === 'string') bVal = bVal.toLowerCase()
    if (aVal < bVal) return sortDir.value === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDir.value === 'asc' ? 1 : -1
    return 0
  })
})

const totalPages  = computed(() => Math.max(1, Math.ceil(filtered.value.length / pageSize.value)))
const paginated   = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return filtered.value.slice(start, start + pageSize.value)
})
const pageNumbers = computed(() => {
  const pages = []
  for (let i = 1; i <= totalPages.value; i++) pages.push(i)
  return pages
})

function formatNum(val) {
  if (val == null || val === '') return '—'
  return Number(val).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatTaux(val) {
  if (val == null || val === '') return '—'
  return `${formatNum(val)} $`
}

function copyTable() {
  const cols = ['Code', 'Description', 'Quantité', 'Unité']
  if (props.can_see_costs) cols.push('Taux unitaire')
  cols.push('Actif')
  const rows = filtered.value.map(p => {
    const r = [p.code, p.description, formatNum(p.quantite), p.unite || '']
    if (props.can_see_costs) r.push(formatTaux(p.taux_unitaire))
    r.push(p.actif ? 'Actif' : 'Inactif')
    return r.join('\t')
  })
  navigator.clipboard.writeText([cols.join('\t'), ...rows].join('\n'))
    .then(() => alert('Tableau copié dans le presse-papiers'))
    .catch(() => alert('Impossible de copier'))
}

function exportCsv() {
  const cols = ['Code', 'Description', 'Quantité', 'Unité']
  if (props.can_see_costs) cols.push('Taux unitaire')
  cols.push('Actif')
  const rows = filtered.value.map(p => {
    const r = [`"${p.code}"`, `"${p.description || ''}"`, `"${formatNum(p.quantite)}"`, `"${p.unite || ''}"`]
    if (props.can_see_costs) r.push(`"${formatTaux(p.taux_unitaire)}"`)
    r.push(p.actif ? 'Actif' : 'Inactif')
    return r.join(',')
  })
  const csv  = '\ufeff' + [cols.join(','), ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = Object.assign(document.createElement('a'), { href: url, download: 'estimation_partie_suivie_elements.csv' })
  a.click()
  URL.revokeObjectURL(url)
}

function printTable() { window.print() }

function toggleActif(p) {
  const nom = p.code
  const msg = p.actif
    ? (p.dependant
        ? `« ${nom} » est utilisé dans des rapports et sera désactivé. Continuer ?`
        : `Supprimer définitivement « ${nom} » ?`)
    : `Réactiver « ${nom} » ?`
  if (!confirm(msg)) return
  router.delete(p.destroy_url, { preserveScroll: true })
}
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="relative w-80 group">
        <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-sm pointer-events-none">search</span>
        <input
          v-model="search"
          type="text"
          placeholder="Rechercher un élément..."
          class="w-full bg-surface-container-highest border-none rounded-md pl-10 pr-4 py-2 text-sm text-on-surface placeholder-outline focus:ring-2 focus:ring-primary/20 outline-none transition-all"
        />
      </div>
    </template>

    <div class="p-8" @click="openDropdown = null">

      <!-- Header -->
      <div class="flex justify-between items-end mb-6">
        <div>
          <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase mb-2">
            Éléments — {{ partie_suivie.code }}
          </h1>
          <p class="text-outline text-xs font-black uppercase tracking-widest mb-2">
            <span v-if="projet.code">
              Projet <a :href="projet.show_url" class="text-primary hover:brightness-125 transition-all">{{ projet.code }}</a>
              &rsaquo;
            </span>
            Estimation <a :href="estimation.show_url" class="text-primary hover:brightness-125 transition-all">{{ estimation.code }}</a>
          </p>
          <div class="flex items-center gap-4 text-[10px] font-black text-outline uppercase tracking-widest">
            <span class="flex items-center gap-1.5">
              <span class="w-2 h-2 rounded-full bg-tertiary"></span>
              {{ elements.filter(p => p.actif).length }} Actifs
            </span>
            <span class="flex items-center gap-1.5">
              <span class="w-2 h-2 rounded-full bg-outline/50"></span>
              {{ elements.filter(p => !p.actif).length }} Inactifs
            </span>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2 bg-surface-container-low px-3 py-2 rounded-md border border-outline-variant/10">
            <span class="text-[10px] font-black uppercase tracking-widest text-outline">Affichage</span>
            <select
              v-model="pageSize"
              class="bg-transparent border-none text-xs font-black text-primary p-0 focus:ring-0 cursor-pointer uppercase outline-none"
            >
              <option :value="10">10 Items</option>
              <option :value="25">25 Items</option>
              <option :value="50">50 Items</option>
            </select>
          </div>
          <a
            :href="back_url"
            class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
          >
            <span class="material-symbols-outlined text-base">arrow_back</span>
            Retour
          </a>
          <a
            v-if="can_create"
            :href="new_url"
            class="machined-sheen px-5 py-2.5 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg"
          >
            <span class="material-symbols-outlined text-sm">add</span>
            Nouvel élément
          </a>
        </div>
      </div>

      <!-- Onglets -->
      <div class="flex items-center gap-1 mb-6">
        <div class="px-4 py-2 text-xs font-black uppercase tracking-widest rounded-sm bg-primary text-on-primary">
          Liste des éléments
        </div>
        <a
          v-if="can_import"
          :href="import_url"
          class="px-4 py-2 text-xs font-black uppercase tracking-widest rounded-sm text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 transition-all"
        >Importer</a>
      </div>

      <!-- Table -->
      <div class="bg-surface-container rounded-xl overflow-hidden shadow-2xl border border-outline-variant/10">
        <table class="w-full text-left border-collapse print-table">
          <thead>
            <tr class="bg-surface-container-high/60 border-b border-outline-variant/15">
              <th v-for="col in [
                { key: 'code',          label: 'Code',         show: true },
                { key: 'description',   label: 'Description',  show: true },
                { key: 'quantite',      label: 'Quantité',     show: true },
                { key: 'unite',         label: 'Unité',        show: true },
                { key: 'taux_unitaire', label: 'Taux unitaire', show: can_see_costs },
                { key: 'actif',         label: 'Statut',       show: true },
              ].filter(c => c.show)" :key="col.key"
                class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline cursor-pointer select-none hover:text-on-surface transition-colors"
                @click="toggleSort(col.key)"
              >
                <span class="inline-flex items-center gap-1">
                  {{ col.label }}
                  <span class="material-symbols-outlined text-[12px] transition-colors"
                    :class="sortKey === col.key ? 'text-primary' : 'text-outline/40'"
                  >{{ sortKey === col.key && sortDir === 'desc' ? 'arrow_downward' : 'arrow_upward' }}</span>
                </span>
              </th>
              <th v-if="can_update" class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline text-right w-20">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-outline-variant/10">
            <tr
              v-for="(p, i) in paginated"
              :key="p.id"
              :class="[
                'group hover:bg-surface-container-highest/20 transition-colors',
                !p.actif ? 'opacity-60' : '',
                i % 2 === 1 ? 'bg-surface-container-low/20' : '',
              ]"
            >
              <td class="px-6 py-4 align-top">
                <a :href="p.show_url" class="font-mono text-sm font-bold text-primary hover:brightness-125 transition-all">{{ p.code }}</a>
              </td>
              <td class="px-6 py-4 align-top text-sm text-on-surface">{{ p.description || '—' }}</td>
              <td class="px-6 py-4 align-top font-mono text-sm text-on-surface/80 text-right">{{ formatNum(p.quantite) }}</td>
              <td class="px-6 py-4 align-top text-sm text-on-surface/80">{{ p.unite || '—' }}</td>
              <td v-if="can_see_costs" class="px-6 py-4 align-top font-mono text-sm font-semibold text-secondary text-right">
                {{ formatTaux(p.taux_unitaire) }}
              </td>
              <td class="px-6 py-4 align-top">
                <div v-if="p.actif" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-tertiary/10 border border-tertiary/20">
                  <span class="w-1.5 h-1.5 rounded-full bg-tertiary animate-pulse"></span>
                  <span class="text-[9px] font-black uppercase tracking-widest text-tertiary">Actif</span>
                </div>
                <div v-else class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-surface-container-highest border border-outline-variant/20">
                  <span class="w-1.5 h-1.5 rounded-full bg-outline/50"></span>
                  <span class="text-[9px] font-black uppercase tracking-widest text-outline">Inactif</span>
                </div>
              </td>
              <td v-if="can_update" class="px-6 py-4 align-top text-right">
                <div class="relative inline-block">
                  <button
                    @click.stop="toggleDropdown(p.id)"
                    class="inline-flex p-2 text-outline hover:text-primary hover:bg-primary/10 rounded transition-all"
                  >
                    <span class="material-symbols-outlined text-lg">edit_note</span>
                  </button>
                  <div
                    v-if="openDropdown === p.id"
                    class="absolute right-0 top-full z-50 mt-1 bg-surface-container-high border border-outline-variant/20 rounded-lg shadow-xl min-w-[160px] py-1"
                  >
                    <a :href="p.show_url" class="flex items-center gap-2 px-4 py-2 text-sm text-outline hover:text-on-surface hover:bg-surface-container-highest transition-colors">
                      <span class="material-symbols-outlined text-base">visibility</span>
                      Voir
                    </a>
                    <a :href="p.edit_url" class="flex items-center gap-2 px-4 py-2 text-sm text-outline hover:text-on-surface hover:bg-surface-container-highest transition-colors">
                      <span class="material-symbols-outlined text-base">edit_note</span>
                      Modifier
                    </a>
                    <button
                      class="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-surface-container-highest transition-colors"
                      :class="p.actif ? 'text-outline hover:text-error' : 'text-outline hover:text-tertiary'"
                      @click.stop="toggleActif(p); openDropdown = null"
                    >
                      <span class="material-symbols-outlined text-base">{{ p.actif ? 'toggle_off' : 'toggle_on' }}</span>
                      {{ p.actif ? (p.dependant ? 'Désactiver' : 'Effacer') : 'Activer' }}
                    </button>
                  </div>
                </div>
              </td>
            </tr>
            <tr v-if="paginated.length === 0">
              <td :colspan="5 + (can_see_costs ? 1 : 0) + (can_update ? 1 : 0)"
                  class="px-6 py-16 text-center text-outline text-sm">
                <span class="material-symbols-outlined text-4xl block mb-3 opacity-30">inventory_2</span>
                Aucun élément trouvé{{ search ? ` pour "${search}"` : '' }}.
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination footer -->
        <div class="px-6 py-4 bg-surface-container-high/40 border-t border-outline-variant/15 flex justify-between items-center">
          <div class="text-[10px] text-outline font-black uppercase tracking-widest">
            Affichage de {{ filtered.length === 0 ? 0 : (currentPage - 1) * pageSize + 1 }}
            à {{ Math.min(currentPage * pageSize, filtered.length) }}
            sur {{ filtered.length }} élément{{ filtered.length > 1 ? 's' : '' }}
          </div>
          <div class="flex gap-1">
            <button
              :disabled="currentPage === 1"
              class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:opacity-30 transition-colors"
              @click="currentPage--"
            >
              <span class="material-symbols-outlined text-base">chevron_left</span>
            </button>
            <template v-for="p in pageNumbers" :key="p">
              <button
                v-if="p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1"
                :class="[
                  'w-8 h-8 flex items-center justify-center rounded text-[10px] font-black transition-colors',
                  p === currentPage
                    ? 'bg-primary text-on-primary'
                    : 'bg-surface-container-low border border-outline-variant/10 text-outline hover:text-primary'
                ]"
                @click="currentPage = p"
              >{{ p }}</button>
              <span
                v-else-if="p === 2 || p === totalPages - 1"
                class="w-8 h-8 flex items-center justify-center text-outline text-sm"
              >…</span>
            </template>
            <button
              :disabled="currentPage === totalPages"
              class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:opacity-30 transition-colors"
              @click="currentPage++"
            >
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Boutons export -->
      <div class="flex items-center gap-3 mt-5 no-print">
        <button
          class="flex items-center gap-2 px-6 py-4 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all hover:brightness-110 active:scale-95"
          @click="exportCsv"
        >
          <span class="material-symbols-outlined text-base">download</span>
          Excel / CSV
        </button>
        <button
          class="flex items-center gap-2 px-6 py-4 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all hover:brightness-110 active:scale-95"
          @click="printTable"
        >
          <span class="material-symbols-outlined text-base">print</span>
          Imprimer
        </button>
        <button
          class="flex items-center gap-2 px-6 py-4 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all hover:brightness-110 active:scale-95"
          @click="copyTable"
        >
          <span class="material-symbols-outlined text-base">content_copy</span>
          Copier
        </button>
      </div>

      <!-- Footer -->
      <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
        Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
      </footer>

    </div>
  </AppLayout>
</template>

<style>
@media print {
  .no-print { display: none !important; }
  body { background: white !important; color: black !important; }
  .print-table th, .print-table td { color: black !important; border: 1px solid #ccc !important; }
}
</style>
